import { get, isEmpty } from "lodash"
import { produce } from "immer"
import { useMemo } from "react"
import { useDispatch } from "react-redux"
import PropTypes from "prop-types"
import { ERROR, LOADING } from "helpers/data-statuses"
import { getRelativeOrCustomPeriod } from "helpers/dates"
import createModule from "modules/createModule"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { getPortfolioId, getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import Compare from "connected-components/compare"
import Error from "components/error"
import Loading from "components/loading"
import { useInitializeQueryStringWithPreferences } from "hooks/query-string"
import PreferenceContext from "hooks/preferences"
import { getContextBase } from "./selectors"

const COMPARE_PREFERENCE_TYPE = "portfolio.compare"
const PREFERENCE_TYPES = [
  {
    type: COMPARE_PREFERENCE_TYPE,
    subtypes: ["positionSets", "selectedMetric"],
  },
  {
    type: "lastSelectedPeriod",
  },
]

function mapPreferencesToQueryString(preferences = {}) {
  if (isEmpty(preferences)) {
    return preferences
  }
  return produce(preferences, draft => {
    const lastSelectedPeriod = draft.lastSelectedPeriod
    delete draft["lastSelectedPeriod"]
    draft.period = getRelativeOrCustomPeriod(lastSelectedPeriod)
  })
}

function CompareViewAdapter(props) {
  const { accountId, portfolioId } = props
  const preferencesProps = useMemo(() => ({ accountId, key: portfolioId, type: COMPARE_PREFERENCE_TYPE }), [accountId, portfolioId])
  const preferencesStatus = useInitializeQueryStringWithPreferences(accountId, portfolioId, PREFERENCE_TYPES, mapPreferencesToQueryString)

  const dispatch = useDispatch()
  const handleSetPeriod = (period) => {
    dispatch(PortfolioRootCreators.setPeriod(period))
  }

  const mainPositionSet = useMemo(() => ({
    type: "PORTFOLIO",
    id: portfolioId,
  }), [portfolioId])

  if (preferencesStatus === ERROR) {
    return <Error />
  }

  if (preferencesStatus === LOADING) {
    return <Loading />
  }

  return <PreferenceContext.Provider value={preferencesProps}>
    <Compare {...props} mainPositionSet={mainPositionSet} setPeriod={handleSetPeriod} />
  </PreferenceContext.Provider>
}

CompareViewAdapter.propTypes = {
  accountId: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  portfolioAlias: PropTypes.string.isRequired,
}

export default createModule({
  externalProps: state => ({
    account: state.global.selected.account,
  }),
  required: ["account"],
  mapStateToProps: state => ({
    accountId: get(state, "global.selected.accountId"),
    portfolioId: getPortfolioId(state),
    modelId: getSelectedModelId(state),
    portfolioAlias: state.global.selected.portfolio,
    latestDate: getLatestDate(state),
    earliestDate: getEarliestDate(state),
    contextBase: getContextBase(state),
  }),
  view: CompareViewAdapter,
})
